<template>
  <div class="products-carousel">
    <!-- Loading skeleton if isLoading is true -->
    <div v-if="isLoading" class="row">
      <div v-for="i in 4" :key="`skeleton-${i}`" class="col-md-3">
        <div class="product-skeleton">
          <div class="skeleton-img"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-text short"></div>
          <div class="skeleton-btn"></div>
        </div>
      </div>
    </div>

    <!-- Carousel when products are loaded -->
    <div v-else class="position-relative">
      <div id="productsCarousel" class="carousel slide" data-bs-ride="carousel">
        <div class="carousel-inner">
          <div 
            v-for="(group, index) in productGroups" 
            :key="index" 
            class="carousel-item" 
            :class="{ active: index === activeSlide }"
          >
            <div class="row g-4">
              <div 
                v-for="product in group" 
                :key="product.id" 
                class="col-6 col-md-3"
              >
                <ProductCard :product="product" />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Carousel Indicators -->
        <div class="carousel-indicators" v-if="productGroups.length > 1">
          <button 
            v-for="(_, index) in productGroups" 
            :key="`indicator-${index}`" 
            type="button" 
            data-bs-target="#productsCarousel" 
            :data-bs-slide-to="index" 
            :class="{ active: index === activeSlide }" 
            :aria-current="index === activeSlide" 
            :aria-label="`Slide ${index + 1}`"
            @click="setActiveSlide(index)"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps } from 'vue';
import ProductCard from '@/components/ProductCard.vue';

const props = defineProps({
  products: {
    type: Array,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  itemsPerSlide: {
    type: Number,
    default: 4
  }
});

// Track active slide index
const activeSlide = ref(0);

// Group products into slides
const productGroups = computed(() => {
  const groups = [];
  const productsArr = Array.isArray(props.products) ? props.products : [];
  const itemsPerRow = window.innerWidth < 768 ? 2 : props.itemsPerSlide;
  
  for (let i = 0; i < productsArr.length; i += itemsPerRow) {
    groups.push(productsArr.slice(i, i + itemsPerRow));
  }
  return groups;
});

// Manual slide navigation methods
const nextSlide = () => {
  activeSlide.value = (activeSlide.value + 1) % productGroups.value.length;
};


// Set active slide directly when clicking an indicator
const setActiveSlide = (index) => {
  activeSlide.value = index;
};

// Handle window resize to update itemsPerRow
window.addEventListener('resize', () => {
  if (window.innerWidth < 768) {
    // Mobile, 2 items per slide
    const newGroups = [];
    const productsArr = Array.isArray(props.products) ? props.products : [];
    
    for (let i = 0; i < productsArr.length; i += 2) {
      newGroups.push(productsArr.slice(i, i + 2));
    }
  } else {
    // Desktop, 4 items per slide
    const newGroups = [];
    const productsArr = Array.isArray(props.products) ? props.products : [];
    
    for (let i = 0; i < productsArr.length; i += 4) {
      newGroups.push(productsArr.slice(i, i + 4));
    }
  }
});

// Watch for changes in products to reset carousel
watch(() => props.products, () => {
  activeSlide.value = 0;
});

// Auto-rotate carousel
onMounted(() => {
  const interval = setInterval(() => {
    if (productGroups.value.length > 1) {
      nextSlide();
    }
  }, 6000);

  return () => {
    clearInterval(interval);
  };
});
</script>

<style scoped>
.products-carousel {
  position: relative;
  margin-bottom: 3rem;
}

.carousel-control-prev,
.carousel-control-next {
  width: 40px;
  height: 40px;
  background: var(--primary-color);
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.8;
}

.carousel-control-prev {
  left: -20px;
}

.carousel-control-next {
  right: -20px;
}

.carousel-control-prev-icon,
.carousel-control-next-icon {
  width: 20px;
  height: 20px;
}

.carousel-indicators {
  position: absolute;
  bottom: -60px;
  z-index: 2;
}

.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: 0 5px;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.carousel-indicators button.active {
  background-color: var(--primary-color);
  width: 12px;
  height: 12px;
}

/* Skeleton loading styles */
.product-skeleton {
  background: var(--black-color-2);
  border-radius: 1rem;
  padding: 1rem;
  height: 320px;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.skeleton-img {
  height: 180px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.skeleton-text {
  height: 20px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-btn {
  height: 40px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-top: auto;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 767px) {
  .carousel-control-prev,
  .carousel-control-next {
    display: none;
  }
  
  .carousel-indicators {
    bottom: -30px;
  }
}
</style>